import { useCallback } from "react";
import { useToastStore, type ToastType } from "@/stores/toast-store";

export function useToast() {
  const addToast = useToastStore((state) => state.addToast);
  const removeToast = useToastStore((state) => state.removeToast);

  const toast = useCallback(
    (input: string | { title?: string; description?: string; variant?: "default" | "destructive" | "success" }, type?: ToastType, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
      // Support shadcn/ui style API: toast({ title, description, variant })
      if (typeof input === "object" && input !== null) {
        const message = input.description || input.title || "";
        const variant = input.variant || "default";
        const toastType: ToastType = variant === "destructive" ? "error" : variant === "success" ? "success" : "info";
        return addToast({
          message,
          type: toastType,
          duration: options?.duration,
          action: options?.action,
        });
      }
      
      // Support legacy API: toast(message, type, options)
      return addToast({
        message: input,
        type: type || "info",
        duration: options?.duration,
        action: options?.action,
      });
    },
    [addToast],
  );

  const success = useCallback(
    (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
      return toast(message, "success", options);
    },
    [toast],
  );

  const error = useCallback(
    (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
      return toast(message, "error", options);
    },
    [toast],
  );

  const info = useCallback(
    (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
      return toast(message, "info", options);
    },
    [toast],
  );

  const warning = useCallback(
    (message: string, options?: { duration?: number; action?: { label: string; onClick: () => void } }) => {
      return toast(message, "warning", options);
    },
    [toast],
  );

  return {
    toast,
    success,
    error,
    info,
    warning,
    remove: removeToast,
  };
}
